import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { ApiKeyListItemDto, invalidateApiKeysCache, useSetApiKeyLocalities } from 'src/api/api-keys'
import { useGetLocalitiesMinimalData } from 'src/api/localities'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { FormSwitch } from 'src/components/FormSwitch'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { buildSetLocalitiesRequest, initialAccessForm } from './ApiAccessPage.action'
import { LocalityMultiSelect } from './LocalityMultiSelect'

interface EditApiKeyLocalitiesDialogProps extends ShowDialogProps {
  customerId: EntityId
  apiKey: ApiKeyListItemDto
}

const editLocalitiesSchema = z.object({
  localityIds: z.array(z.number()),
  allLocalities: z.boolean(),
})

type EditLocalitiesFormValues = z.infer<typeof editLocalitiesSchema>

const EditApiKeyLocalitiesDialog: React.FC<EditApiKeyLocalitiesDialogProps> = ({ pop, customerId, apiKey }) => {
  const { t } = useTranslation(['api-access', 'common', 'common-api-message'])
  const { data: localities } = useGetLocalitiesMinimalData(customerId)
  const setLocalitiesMut = useSetApiKeyLocalities(customerId, apiKey.id)

  const form = useZodForm(editLocalitiesSchema, {
    defaultValues: initialAccessForm(apiKey),
  })

  const handleSubmit = (values: EditLocalitiesFormValues) => {
    setLocalitiesMut.mutate(buildSetLocalitiesRequest(values), {
      onSuccess: () => {
        invalidateApiKeysCache.list(customerId)
        pop(true)
      },
      onError: (error: any) => {
        toast.error(t(error?.data || 'unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit} hasInitialValues>
      <GenericDialogLayout
        title={t('edit-localities-title', { ns: 'api-access' })}
        pop={() => pop()}
        actions={[
          <FormSubmitButton
            key="submit"
            isLoading={setLocalitiesMut.isLoading}
            updateText={t('save', { ns: 'common' })}
          />,
        ]}
      >
        <FormSwitch
          name="allLocalities"
          label={t('full-access-label', { ns: 'api-access' })}
          onValueChange={checked => checked && form.setValue('localityIds', [])}
        />
        <LocalityMultiSelect
          control={form.control}
          name="localityIds"
          options={localities ?? []}
          label={t('localities', { ns: 'api-access' })}
          helperText={t('localities-helper', { ns: 'api-access' })}
          disabled={form.watch('allLocalities')}
        />
      </GenericDialogLayout>
    </AppForm>
  )
}

export default EditApiKeyLocalitiesDialog
